import { request } from "@/utils/request";

/**
 * 根据起运港和目的港查询船期列表
 * @param {string} eta 固定空字符串
 * @param {string} etd 离港日期
 * @param {string} isTransit 是否中转(0:直达,1:中转)
 * @param {number} pageNum 页码
 * @param {number} pageSize 每页条数
 * @param {string} podCode 目的港代码
 * @param {string} polCode 起运港代码
 * @param {number} weeksOut 周数(固定传8)
 * @returns {Promise<Array<Object>>} 船期列表
 */
export const queryShipSchedule = (param) => {
    return request("/app/user-p2p-shipping/shipping-data/list", "post", param);
};

/**
 * 根据起运港，目的港，日期，vessel查group
 * @param {string} eta 固定空字符串
 * @param {string} etd 离港日期
 * @param {string} isTransit 是否中转(0:直达,1:中转)
 * @param {number} pageNum 页码
 * @param {number} pageSize 每页条数
 * @param {string} podCode 目的港代码
 * @param {string} polCode 起运港代码
 * @param {number} weeksOut 周数(固定传8)
 * @param {string} vessel 船名
 * @param {string} id // 历史记录id
 */
export const queryShipScheduleGroupByVessel = (param) => {
    return request(
        "/app/user-p2p-shipping/shipping-data/listGroupByVessel",
        "post",
        param,
    );
};

/**
 * 根据起运港和目的港还有分组名查询船期列表
 * @param {string} carriercd 船公司代码(非必传，筛选条件)
 * @param {string} eta 固定空字符串
 * @param {string} etd 离港日期
 * @param {string} isTransit 是否中转(0:直达,1:中转)
 * @param {number} pageNum 页码
 * @param {number} pageSize 每页条数
 * @param {string} podCode 目的港代码
 * @param {string} podTerminal 目的码头(非必传，筛选条件)
 * @param {string} polCode 起运港代码
 * @param {string} polTerminal 起运码头(非必传，筛选条件)
 * @param {number} weeksOut 周数(固定传8)
 * @param {string} displayGroup 分组名称
 * @param {string} vessel 船名(非必传，筛选条件，如果要用船名筛选，先用船名传入vessel获取displayGroup,再带上displayGroup和这个参数)
 * @param {number} id // 历史记录id
 * @returns {Promise<Array<Object>>} 船期列表
 */
export const queryShipScheduleByGroupName = (param) => {
    return request(
        "/app/user-p2p-shipping/shipping-data/listByGroup",
        "post",
        param,
    );
};

/**
 * 用户查询历史记录
 * @param {string} polCode 起运港代码(非必传)
 * @param {string} podCode 目的港代码(非必传)
 * @param {string} etd 离港日期(非必传)
 * @param {number} page 页码
 * @param {number} limit 每页条数
 * @param {string} remark 备注(非必传)
 * @returns {Promise<Array<Object>>} 历史记录
 */
export const queryShipScheduleHistory = (param) => {
    return request("/app/user-p2p-shipping/list", "post", param);
};

/**
 * 用户删除历史记录
 * @param {Array<number>} ids 历史记录id数组
 * @returns {Promise<Object>} 删除结果
 */
export const deleteShipScheduleHistory = (param) => {
    return request("/app/user-p2p-shipping/delete", "post", param);
};

/**
 * 根据船名和航次查询船舶计划
 * @param {string} vesselName 船名
 * @param {string} voyage 航次
 * @returns {Promise<Array<Object>>} 船舶计划
 */
export const queryShipPlan = (data, token) => {
    return request(
        "/app/user-p2p-shipping/shipping-data/getVesselShipmentsPlan",
        "post",
        data,
        {
            headers: {
                Authorization: token,
            },
        },
    );
};

/**
 * 根据船名和航次查询码头计划
 * @param {string} vesselName 船名
 * @param {string} voyage 航次
 * @param {string} portCode 港口代码
 * @param {string} isExport（固定传空）
 * @returns {Promise<Array<Object>>} 码头计划
 */
export const queryTerminalPlan = (data, token) => {
    return request(
        "/app/user-p2p-shipping/shipping-data/getTerminalPlan",
        "post",
        data,
        {
            headers: {
                Authorization: token,
            },
        },
    );
};

/**
 * 获取船信息
 * @param {string} mmsi
 */
export const getShipInfoByMmsi = (params, token) => {
    return request(
        "/app/user-p2p-shipping/shipping-data/getVesselInfoBymmsi",
        "get",
        params,
        {
            headers: {
                "Content-Type": "application/x-www-form-urlencoded",
                Authorization: token,
            },
        },
    );
};

/**
 * 获取船当前位置
 * @param {string} mmsi
 * @returns {Promise<Object>} 船当前位置
 */
export const shipScheduleGetCurrentPosition = (params, token) => {
    return request(
        "/app/user-p2p-shipping/shipping-data/getShipLocation",
        "get",
        params,
        {
            headers: {
                Authorization: token,
            },
        },
    );
};

/**
 * 获取船轨迹
 * @param {string} mmsi
 * @param {string} startTime
 * @param {string} endTime
 * @returns {Promise<Object>} 船轨迹
 */
export const shipScheduleGetTrack = (data, token) => {
    return request(
        "/app/user-p2p-shipping/shipping-data/getVesselTrack",
        "post",
        data,
        {
            headers: {
                Authorization: token,
            },
        },
    );
};

/**
 * 港口代码模糊查询
 * @param {string} port 名称或代码
 */
export const queryFeiTuoPortCode = (params) => {
    return request("/p2pShipping/ft/port/fcw/likePortInfo", "get", params, {
        headers: {
            "Content-Type": "application/x-www-form-urlencoded",
        },
    });
};

/**
 * 获取全部飞陀港口代码
 */
export const getAllFeiTuoPortCode = () => {
    return request(
        "/p2pShipping/ft/port/fcw/getmessage",
        "get",
        {},
        {
            headers: {
                "Content-Type": "application/x-www-form-urlencoded",
            },
        },
    );
};
